import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useMemo, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import AddressGroupV2 from 'components/field/AddressGroupV2';
import AutoComplete from 'components/field/AutoComplete';
import Badge from 'components/field/Badge';
import Button from 'components/field/Button';
import PhoneNumber from 'components/field/PhoneNumber';
import ProgressWizard from 'components/field/ProgressWizard';
import StringInput from 'components/field/StringInput';
import FormItem from 'components/form/FormItem';
import Image from 'components/global/Image';
import PageHeader from 'components/global/PageHeader';
import Radio from 'components/global/Radio';
import Typography from 'components/global/Typography';
import { PageHeaderContent } from 'components/global/styled/PageHeader.styled';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';

import fetchRemainingAmount from 'services/common/fetchRemainingAmount';
import { AUTOCOMPLETE, OTO_STORE } from 'services/consts';

import { setLoading } from 'stores/layoutSlice';

import AppLogger from 'utils/AppLogger';
import { PAYMENT_METHODS } from 'utils/constants';
import PATHS from 'utils/paths';

import { faCheck, faChevronLeft, faChevronRight } from '@fortawesome/pro-solid-svg-icons';

import { useTranslateProductName } from './OTOStore';
import * as S from './OTOStoreCheckout.styled';

const defaultColSpan = {
	md: 6
};

const validateOptions = {
	shouldValidate: true,
	shouldDirty: true
};

const SHIPMENT_TYPES = {
	WAREHOUSE: 'warehouse',
	OTHER_ADDRESS: 'otherAddress'
};

const OTOStoreCheckout = () => {
	const axiosPrivate = useAxiosPrivate();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { showError } = useAlert();
	const { Country } = useLocale();
	const [currentTab, setCurrentTab] = useState(0);
	const userCart = useSelector((state) => state.otoStore.cart);
	const [orderItems, setOrderItems] = useState(null);
	const [isCheckoutSuccess, setIsCheckoutSuccess] = useState(false);

	const wizardTabs = useMemo(() => {
		return [
			{
				name: 'shippingInformation'
			},
			{
				name: 'paymentMethod',
				success: isCheckoutSuccess
			}
		];
	}, [isCheckoutSuccess]);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					shipmentType: yup.string().nullable().required(),
					warehouseID: yup
						.string()
						.nullable()
						.when('shipmentType', {
							is: SHIPMENT_TYPES.WAREHOUSE,
							then: (schema) => schema.required()
						}),

					fullName: yup
						.string()
						.nullable()
						.when('shipmentType', {
							is: SHIPMENT_TYPES.OTHER_ADDRESS,
							then: (schema) => schema.required()
						}),
					phone: yup
						.string()
						.nullable()
						.when('shipmentType', {
							is: SHIPMENT_TYPES.OTHER_ADDRESS,
							then: (schema) => schema.required()
						}),
					email: yup
						.string()
						.email()
						.nullable()
						.when('shipmentType', {
							is: SHIPMENT_TYPES.OTHER_ADDRESS,
							then: (schema) => schema.required()
						}),
					address: yup.object().when('shipmentType', {
						is: SHIPMENT_TYPES.OTHER_ADDRESS,
						then: () =>
							yup.object({
								text: yup.string().nullable().required(),
								countryName: yup.string().nullable().required(),
								stateName: yup.string().nullable(),
								cityName: yup.string().nullable().required(),
								districtName: yup.string().nullable(),
								street: yup.string().nullable(),
								buildingName: yup.string().nullable(),
								lat: yup.number().min(-90).max(90).nullable(),
								lng: yup.number().min(-180).max(180).nullable(),
								zipCode: yup.string().nullable(),
								addressInputType: yup.string().nullable()
							})
					})
				})
				.required(),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			shipmentType: SHIPMENT_TYPES.WAREHOUSE,
			paymentMethod: PAYMENT_METHODS.OTO_CREDIT,
			warehouseID: null,
			fullName: null,
			phone: null,
			email: null,
			address: {
				text: null,
				countryName: Country,
				stateName: null,
				cityName: null,
				districtName: null,
				street: null,
				buildingName: null,
				lat: null,
				lng: null,
				zipCode: null,
				addressInputType: null
			}
		}
	});

	const {
		handleSubmit,
		formState: { isSubmitting, isLoading: isFormLoading }
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;

	const renderTab = useMemo(() => {
		switch (currentTab) {
			case 0:
				return <ShippingInformation />;
			case 1:
				return <PaymentMethod />;
			default:
				return null;
		}
	}, [currentTab]);

	const nextTab = () => {
		if (currentTab < wizardTabs.length - 1) {
			setCurrentTab(currentTab + 1);
		}
	};

	const prevTab = () => {
		if (currentTab > 0) {
			setCurrentTab(currentTab - 1);
		}
	};

	const handleCancel = () => {
		navigate(PATHS.OTO_STORE);
	};

	const onCreateOrder = async (data) => {
		try {
			setOrderItems(JSON.parse(JSON.stringify(userCart.items)));
			dispatch(setLoading(true));
			const { shipmentType, paymentMethod, warehouseID, fullName, phone, email, address } = data;
			const response = await axiosPrivate.post(
				OTO_STORE.CREATE_ORDER,
				shipmentType === SHIPMENT_TYPES.WAREHOUSE
					? { paymentMethod, warehouseId: warehouseID }
					: {
							paymentMethod,
							fullName,
							phone,
							email,
							address
						}
			);
			if (response.status === 200) {
				setIsCheckoutSuccess(true);
				fetchRemainingAmount();
			}
		} catch (error) {
			AppLogger.error(error);
			showError(
				error?.response?.data?.code && {
					message: error.response.data.code
				}
			);
		} finally {
			dispatch(setLoading(false));
		}
	};

	return (
		<div>
			<PageHeader
				title="checkout"
				tooltip="otoStoreCheckoutTooltip"
				lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/oto-store.json.gz">
				<S.PageHeaderContainer>
					<PageHeaderContent>
						<ProgressWizard
							stages={wizardTabs}
							index={currentTab}
							setIndex={setCurrentTab}
							clickable={false}
						/>
					</PageHeaderContent>
				</S.PageHeaderContainer>
			</PageHeader>
			{isCheckoutSuccess ? (
				<Success orderItems={orderItems} />
			) : (
				<S.PageContainer>
					<FormProvider {...formMethods}>
						<S.PageContent>{renderTab}</S.PageContent>
						<S.ActionsContainer>
							<Button
								label="cancel"
								variant="blackOutline"
								onClick={handleCancel}
								css={{
									textDecoration: 'underline',
									border: 'none'
								}}
								loading={isLoading}
							/>
							{currentTab > 0 && (
								<Button
									label="previous"
									icon={faChevronLeft}
									variant="outline"
									onClick={prevTab}
									loading={isLoading}
								/>
							)}
							<Button
								label={currentTab !== 1 ? 'continueButton' : 'complete'}
								icon={currentTab !== 1 ? faChevronRight : faCheck}
								iconPos={currentTab !== 1 ? 'right' : 'left'}
								onClick={handleSubmit(currentTab !== 1 ? nextTab : onCreateOrder, (errors) => {
									AppLogger.log(errors);
								})}
								loading={isLoading}
								disabled={userCart?.items?.length === 0}
							/>
						</S.ActionsContainer>
					</FormProvider>
				</S.PageContainer>
			)}
		</div>
	);
};

const SectionTitle = ({ title, subTitle }) => {
	return (
		<S.SectionTitleContainer>
			<Typography variant="p2" fontWeight="700">
				{title}
			</Typography>
			{subTitle && <Typography color="var(--content-3)">{subTitle}</Typography>}
		</S.SectionTitleContainer>
	);
};

const ShippingInformation = () => {
	const formMethods = useFormContext();
	const { watch, setValue } = formMethods;

	return (
		<S.SectionContainer>
			<SectionTitle title="shippingInformation" />

			<S.PickupOptionsButtonContainer>
				<S.PickupOptionsCard
					className={watch('shipmentType') === SHIPMENT_TYPES.WAREHOUSE ? 'selected' : ''}
					onClick={() => setValue('shipmentType', SHIPMENT_TYPES.WAREHOUSE, validateOptions)}>
					<S.PickupOptionsCardContent>
						<Image
							height="59px"
							url={
								'https://storage.googleapis.com/tryoto-public/public/assets/icons/OTO-Store/shipToWarehouse.svg'
							}
							width="unset"
						/>
						<S.PickupOptionsTextContainer>
							<S.PickupOptionsCardTitle>
								<Typography variant="p2" fontWeight="500" noWrap>
									otoStoreShipToMyWarehouse
								</Typography>
								<Badge variant="pill" color="teel" text="fastest" />
							</S.PickupOptionsCardTitle>

							<Typography color="var(--content-3)" noWrap>
								otoStoreShipToMyWarehouseDescription
							</Typography>
						</S.PickupOptionsTextContainer>
					</S.PickupOptionsCardContent>
					<S.StyledRadio readOnly checked={watch('shipmentType') === SHIPMENT_TYPES.WAREHOUSE} />
				</S.PickupOptionsCard>

				<S.PickupOptionsCard
					className={watch('shipmentType') === SHIPMENT_TYPES.OTHER_ADDRESS ? 'selected' : ''}
					onClick={() => setValue('shipmentType', SHIPMENT_TYPES.OTHER_ADDRESS, validateOptions)}>
					<S.PickupOptionsCardContent>
						<Image
							height="59px"
							url={
								'https://storage.googleapis.com/tryoto-public/public/assets/icons/OTO-Store/shipToAnotherAddress.svg'
							}
							width="unset"
						/>
						<S.PickupOptionsTextContainer>
							<Typography variant="p2" fontWeight="500" noWrap>
								otoStoreShipToAnotherAddress
							</Typography>
							<Typography color="var(--content-3)" noWrap>
								otoStoreShipToAnotherAddressDescription
							</Typography>
						</S.PickupOptionsTextContainer>
					</S.PickupOptionsCardContent>
					<S.StyledRadio readOnly checked={watch('shipmentType') === SHIPMENT_TYPES.OTHER_ADDRESS} />
				</S.PickupOptionsCard>
			</S.PickupOptionsButtonContainer>

			<S.Divider />
			{watch('shipmentType') === SHIPMENT_TYPES.WAREHOUSE ? (
				<S.SectionContainer>
					<SectionTitle title="deliveryLocation" />
					<FormItem colSpan={defaultColSpan} label="warehouse" className="p-0" required>
						<AutoComplete
							name="warehouseID"
							autoCompleteName={AUTOCOMPLETE.WAREHOUSES}
							columns={[{ accessor: 'Name' }]}
							selectionLabelAccessor={['Name']}
							value={watch('warehouseID')}
							setValue={(value, isFirstRender) => {
								if (isFirstRender || value?.ID === watch('warehouseID')) return;
								setValue('warehouseID', value?.ID, validateOptions);
							}}
						/>
					</FormItem>
				</S.SectionContainer>
			) : (
				<S.SectionContainer>
					<SectionTitle title="receiverDetails" />
					<S.StyledForm>
						<FormItem label="fullName" colSpan={defaultColSpan} required>
							<StringInput name="fullName" placeholder="fullNamePlaceholder" />
						</FormItem>
						<FormItem colSpan={defaultColSpan} label="email" required>
							<StringInput name="email" placeholder="emailPlaceholder" />
						</FormItem>
						<FormItem colSpan={defaultColSpan} label="mobileNumber" required>
							<PhoneNumber
								name="phone"
								onChange={(value) => {
									setValue('phone', value, { shouldValidate: true, shouldDirty: true });
								}}
								value={watch('phone')}
							/>
						</FormItem>
					</S.StyledForm>

					<S.Divider />

					<SectionTitle title="addressDetail" />
					<div>
						<AddressGroupV2
							formFieldName="address"
							customTextFields={{
								country: false,
								state: true,
								city: false,
								district: true
							}}
						/>
					</div>
				</S.SectionContainer>
			)}
		</S.SectionContainer>
	);
};

const PaymentMethod = () => {
	const userCart = useSelector((state) => state.otoStore.cart);
	const { translateProductName } = useTranslateProductName();
	const formMethods = useFormContext();
	const { watch, setValue } = formMethods;

	const selectedPaymentMethod = watch('paymentMethod');

	const radioChange = ({ currentTarget: { id } }) => {
		setValue('paymentMethod', id);
	};

	return (
		<S.PaymentMethodContainer>
			<S.SectionContainer>
				<SectionTitle title="paymentMethod" subTitle="otoStoreChooseYourPaymentMethod" />

				<S.PaymentOptionsContainer>
					<S.PaymentCard id={PAYMENT_METHODS.OTO_CREDIT} onClick={radioChange}>
						<Radio checked={selectedPaymentMethod === PAYMENT_METHODS.OTO_CREDIT} readOnly />
						<Typography variant="p1" component="span" fontWeight={500}>
							otoCredit
						</Typography>
					</S.PaymentCard>
					{/* <S.PaymentCard id={PAYMENT_METHODS.CREDIT_CARD} onClick={radioChange} disabled>
						<Radio checked={selectedPaymentMethod === PAYMENT_METHODS.CREDIT_CARD} readOnly disabled />
						<Typography variant="p1" component="span" fontWeight={500}>
							creditCard
						</Typography>
					</S.PaymentCard>
					<S.PaymentCard id={PAYMENT_METHODS.BANK_CARD} onClick={radioChange} disabled>
						<Radio checked={selectedPaymentMethod === PAYMENT_METHODS.BANK_CARD} readOnly disabled />
						<Typography variant="p1" component="span" fontWeight={500}>
							ibanPaymentHeader
						</Typography>
					</S.PaymentCard> */}
				</S.PaymentOptionsContainer>
			</S.SectionContainer>

			<S.SummaryContainer>
				<SectionTitle title="reviewYourCart" />

				<S.OrderList>
					{userCart?.items?.map?.((product) => (
						<S.SummaryOrderCard key={product?.productId}>
							<S.SummaryCardRow>
								<Image
									url={product?.image}
									width="6rem"
									height="6rem"
									style={{ borderRadius: 'var(--small-border-radius)' }}
								/>
								<S.SummaryCardTitle>
									<Typography fontWeight="500">{translateProductName(product)}</Typography>
									<Typography variant="caption" color="var(--content-3)">
										<Trans
											i18nKey="otoStoreProductQuantity"
											values={{ quantity: product?.quantity }}
										/>
									</Typography>
								</S.SummaryCardTitle>
							</S.SummaryCardRow>
							<Typography fontWeight="500" withCurrency>
								{product?.itemSubTotal}
							</Typography>
						</S.SummaryOrderCard>
					))}
				</S.OrderList>
				<S.Divider />

				<S.OrderPriceSummary>
					<S.OrderPriceRow>
						<Typography color="var(--content-3)">subTotal</Typography>
						<Typography color="var(--content-3)" fontWeight="500" withCurrency>
							{userCart?.subTotal}
						</Typography>
					</S.OrderPriceRow>
					<S.OrderPriceRow>
						<Typography color="var(--content-3)">shippingFee</Typography>
						<Typography color="var(--content-4)" fontWeight="500" withCurrency>
							{userCart?.shippingTotal}
						</Typography>
					</S.OrderPriceRow>
					{userCart?.discount && (
						<S.OrderPriceRow>
							<Typography color="var(--content-3)">discount</Typography>
							<Typography color="var(--content-4)" fontWeight="500" withCurrency>
								{userCart?.discount}
							</Typography>
						</S.OrderPriceRow>
					)}
				</S.OrderPriceSummary>
				<S.Divider />

				<S.OrderPriceRow className="section-mt">
					<Typography variant="p2" fontWeight="500">
						grandTotal
					</Typography>
					<Typography variant="p1" fontWeight="500" withCurrency>
						{userCart?.grandTotal}
					</Typography>
				</S.OrderPriceRow>
			</S.SummaryContainer>
		</S.PaymentMethodContainer>
	);
};

const Success = ({ orderItems }) => {
	const { translateProductName } = useTranslateProductName();
	const navigate = useNavigate();

	const handleDone = () => {
		navigate(PATHS.OTO_STORE);
		fetchRemainingAmount();
	};

	return (
		<S.SuccessCard>
			<S.SuccessContainer>
				<S.SuccessContent>
					<S.SuccessInfo>
						<Image
							url="https://storage.googleapis.com/tryoto-public/public/assets/icons/payment-success.svg"
							width="20rem"
							height="16rem"
						/>
						<Typography variant="p1" fontWeight="700">
							otoStoreOrderSuccessTitle
						</Typography>
						<Typography variant="p3" color="var(--content-3)" fontWeight="400">
							otoStoreOrderSuccessDescription
						</Typography>
					</S.SuccessInfo>

					<S.SuccessOrderList>
						<SectionTitle title="orderSummary" />
						<S.SuccessOrderCardContainer>
							{orderItems?.map?.((product) => (
								<S.SuccessOrderCard key={product?.productId}>
									<S.SummaryCardRow>
										<Image
											url={product?.image}
											width="6rem"
											height="6rem"
											style={{ borderRadius: 'var(--small-border-radius)' }}
										/>
										<S.SummaryCardTitle>
											<Typography fontWeight="500">{translateProductName(product)}</Typography>
											<Typography variant="caption" color="var(--content-3)">
												<Trans
													i18nKey="otoStoreProductQuantity"
													values={{ quantity: product?.quantity }}
												/>
											</Typography>
										</S.SummaryCardTitle>
									</S.SummaryCardRow>
									<Typography fontWeight="500" withCurrency>
										{product?.itemSubTotal}
									</Typography>
								</S.SuccessOrderCard>
							))}
						</S.SuccessOrderCardContainer>
					</S.SuccessOrderList>
				</S.SuccessContent>
				<S.ActionsContainer>
					<Button label="done" icon={faCheck} iconPos={'left'} onClick={handleDone} />
				</S.ActionsContainer>
			</S.SuccessContainer>
		</S.SuccessCard>
	);
};

export default OTOStoreCheckout;
